import type { Networkish, SupTransactionType, SupWeb3Address, SupWeb3Balance, SupWeb3TokenAddress, SupWeb3TxSingleVault } from "@/types";
import type { KeyedMutator } from "swr";
import { useMemo } from "react";

import useApi from "@/hooks/api/useApi";
import useValidateTxSettings from "@/hooks/tx-flow/settings/useValidateTxSettings";
import useGetRefundAddress from "@/hooks/web3/useGetRefundAddress";
import useWeb3UserAddress from "@/hooks/web3/useWeb3UserAddress";
import { useStoredData } from "@/store/Context";
import { type BigNumberish, FixedNumber } from "@ethersproject/bignumber";

import { type Address, getAddress, parseUnits } from "viem";
import { useWeb3DebugContext } from "@/components/Web3/Debug/State";

export interface SupCalculateRouteService {
  name: string;
  logo: string;
}

export interface SupCalculateRouteToken {
  address: SupWeb3Address;
  chain_id: number;
  decimals: number;
  logo_uri: string;
  name: string;
  symbol: string;
}

export interface SupCalculateRoute {
  route_id: string;
  interim_token: string;
  interim_token_amount: string;
  erc20_route_approval_target: string;

  bridge: SupCalculateRouteService;
  bridge_id: number;
  cross_chain_fee: number;
  dex?: SupCalculateRouteService;
  estimate_time: number;
  from_address: SupWeb3Address;
  from_amount: BigNumberish;
  from_chain_id: number;
  from_token: SupCalculateRouteToken;
  gas_cost_usd: number;
  insurable_route: boolean;
  is_erc20: boolean;
  super_position: CalculateSuperPositions;
  service: SupCalculateRouteService;
  has_swap: boolean;
  to_address: SupWeb3Address;
  to_amount: string;
  to_amount_min: string;
  to_amount_usd: number;
  lifi_route_first_step?: SupLifiStep;
  to_chain_id: number;
  to_token: SupCalculateRouteToken;
}

export type DepositBridges =
  | "across"
  | "anyswap-router-v4"
  | "anyswap-router-v6"
  | "arbitrum-bridge"
  | "base-bridge"
  | "cctp"
  | "celer"
  | "connext"
  | "hop"
  | "hyphen"
  | "optimism-bridge"
  | "stargate"
  | "symbiosis"
  | "synapse"
  | "zksync-native"
  | "zora-bridge"
  | string;
export type BridgeErrorStatus = "BRIDGE_QUOTE_TIMEOUT" | "ASSET_NOT_SUPPORTED" | string;

export interface BridgeRouteErrors {
  bridge: DepositBridges;
  maxAmount: string;
  minAmount: string;
  status: BridgeErrorStatus;
}

export interface SupLifiIncludedStep {
  type: "cross" | "swap";
  tool: string;
  toolDetails: {
    logoURI: string;
  };
  estimate: {
    feeCosts: {
      name: string;
      percentage: number;
      amount: number;
    }[];
    gasCosts: {
      amountUSD: number;
    }[];
  };
}

export interface SupLifiStep {
  estimate: {
    approvalAddress: SupWeb3Address;
    executionDuration: number;
  };
  includedSteps: SupLifiIncludedStep[];
}

export interface CalculateSuperPositions {
  amount: BigNumberish;
  symbol: string;
  usd_value: number;
}

export interface Web3TxCalculateIn {
  exclude_liquidity_providers: number[];
  super_positions: [
    {
      amount: "…";
      symbol: "…";
      usd_value: 1;
    },
  ]; // @todo: check and fix type

  filter_swap_routes: boolean;
  interimSwapToken: string; // @todo: check type
  is_erc20: boolean;
  is_part_of_multi_vault: boolean;
  need_insurance: boolean;
  original_from_chain_id: number;
  output_amount_for_withdraw: BigNumberish;
  output_amount_normalized: BigNumberish;
  permit2: SupWeb3Address;
  positive_slippage: number;
  refund_address: SupWeb3Address;
  retain_4626: boolean;
  swap_from_amount: BigNumberish;
  user_address: SupWeb3Address;
  from_chain_id: Networkish;
  to_chain_id: Networkish;
  from_token_address: SupWeb3TokenAddress;
  to_token_address: SupWeb3TokenAddress;
  amount_in: BigNumberish;
  vault_id: string;
  vault_decimals: number;
  transaction_type: SupTransactionType | string;
  source: any;
  destination: SupWeb3Address;
  is_same_route: boolean;
  is_x_chain: boolean;
  is_non_swap: boolean;
  superform_id: string;
  superform_address: SupWeb3Address;
  adjusted_amount: bigint;
  vault_token: Address;
  contract_address: Address;
  from_address: Address;
  from_amount: BigNumberish;
  form_implementation_id: any;
  liquidity_token: Address;
  method: string;
  max_user_txs: number;
  has_dst_swap: boolean;
  amount_for_dst_swap: BigNumberish;
  processor: any;
  vault_chain_id: Networkish;
  // direct deposit / withdraw
  gas_estimate?: {
    gas_price_usd: string;
    estimated_seconds: number;
  };
  // settings-controlled params
  route_type: string;
  exclude_bridges: any | null;
  exclude_dexes: any | null;
  exclude_ambs: any | null;
  bridge_slippage: number;
  swap_slippage: number;
  max_slippage: number;

  // deposit
  superpositionChainLocation?: Networkish;
  superpositionAmount?: BigNumberish;

  // withdraw
  superpositions_amount_in?: BigNumberish;
  superpositions_chain_id?: BigNumberish;
}

export interface Web3TxCalculateLiq {
  routes: SupCalculateRoute[];
  bridgeRouteErrors?: BridgeRouteErrors[];
}

export interface ResponseTxCalculate {
  in: Web3TxCalculateIn;
  liq: Web3TxCalculateLiq;
}

export interface Web3TxRoutes {
  in: Web3TxCalculateIn;
  liq?: Web3TxCalculateLiq;
  routes: SupCalculateRoute[];
}

export interface Web3TxMultiCalculateLiq {
  ok: boolean;
  routes: SupCalculateRoute[];
  vault_id: string;
  route_error?: string;
}
export interface Web3TxMultiCalculateRoutes {
  in: Web3TxCalculateIn;
  liq: Web3TxMultiCalculateLiq;
}

export interface Web3TxMultiCalcSWR {
  data: Web3TxMultiCalculateRoutes[] | undefined;
  timestamp: number | undefined;
  error: any;
  mutate: KeyedMutator<Web3TxMultiCalculateRoutes[]>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * @package api:go
 * @link https://superform-xyz.postman.co/workspace/Superform~fe57d318-f198-4f97-9c50-0b2a5a78dbac/request/3472327-846103af-a50f-4ec2-97f3-65fd89c27af0?ctx=documentation
 */
export default function useDepositCalculate(
  selectedBalance: SupWeb3Balance | undefined,
  vaultList: SupWeb3TxSingleVault[],
  pauseRequests: boolean = false,
  enabled: boolean = true
): Web3TxMultiCalcSWR {
  const storedData = useStoredData();
  const userAddress = useWeb3UserAddress();
  const { address: refundAddress } = useGetRefundAddress(selectedBalance?.chainId);

  const { validatedSettings } = useValidateTxSettings(storedData.txSettings);

  const { addCalculate } = useWeb3DebugContext();

  const queryParams = useMemo(() => {
    return vaultList.map(singleVault => ({
      user_address: userAddress,
      from_token_address: selectedBalance ? getAddress(selectedBalance.address) : undefined,
      from_chain_id: selectedBalance?.chainId,
      amount_in: singleVault.amount?.toString(),
      refund_address: refundAddress,
      vault_id: singleVault.id,
      bridge_slippage: validatedSettings?.bridgeSlippage,
      swap_slippage: validatedSettings?.dexSlippage,
      route_type: validatedSettings?.routePriority,
      exclude_ambs: validatedSettings?.services.ambs,
      exclude_liquidity_providers: validatedSettings?.services.liquidity_providers,
      exclude_dexes: validatedSettings?.services.dexes,
      exclude_bridges: validatedSettings?.services.bridges,
    }));
  }, [
    vaultList,
    userAddress,
    selectedBalance,
    refundAddress,
    validatedSettings?.bridgeSlippage,
    validatedSettings?.dexSlippage,
    validatedSettings?.routePriority,
    validatedSettings?.services.ambs,
    validatedSettings?.services.liquidity_providers,
    validatedSettings?.services.dexes,
    validatedSettings?.services.bridges,
  ]);

  const isEnabled = useMemo(() => {
    const totalAmount = vaultList.reduce((acc, vault) => (vault.amount ? acc.addUnsafe(vault.amount) : acc), FixedNumber.from("0"));
    const bigIntTotalAmount = parseUnits(totalAmount.round(selectedBalance?.decimals).toString(), selectedBalance?.decimals ?? 18);
    const rawEnabled = !!userAddress && !!bigIntTotalAmount && bigIntTotalAmount > 0n && !pauseRequests && enabled && !!refundAddress && !!selectedBalance && !!validatedSettings;
    if (rawEnabled) {
      addCalculate?.(queryParams);
    }
    return rawEnabled;
  }, [vaultList, selectedBalance, userAddress, pauseRequests, enabled, refundAddress, validatedSettings, addCalculate, queryParams]);

  const { data, isLoading, isError, error, mutate } = useApi<Web3TxMultiCalculateRoutes[], Web3TxMultiCalculateRoutes[]>(
    {
      path: "/deposit/calculate/",
      api: "v2",
      data: queryParams,
      method: "POST",
      useJwt: true,
      forceJwt: false,
    },
    {
      enabled: isEnabled,
      strategy: "90s-interval-refresh",
    }
  );

  const timestamp = useMemo(() => (data !== undefined ? new Date().getTime() : undefined), [data]);

  return {
    mutate,
    data,
    timestamp,
    error,
    isLoading,
    isError,
  };
}
